import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import { Chart } from "chart.js";
import type { ChartConfiguration, Plugin } from "chart.js";

import { drawWithoutFrequencyProfile } from "../../../../../../../components/common/chart-plugins/connecting-lines";

import { panChartOverview } from "./chart-zoom-overview-geometry";
import type { IChartOverviewDomain } from "./chart-zoom-overview-geometry";

import type {
  IAxisZoomBounds,
  ICascadeChartZoomPayload,
  IChartZoomPayload,
  IChartZoomState,
  IItemRangeZoom,
  IViewportZoomBounds,
} from "../../../../../../../components/common/chart-plugins";

export interface IChartZoomControlProps {
  registerReset?: (handler: (() => void) | null) => void;
  onZoomStateChange?: (isZoomed: boolean) => void;
}

export interface IChartZoomOverview {
  imageUrl: string;
  width: number;
  height: number;
  domain: IChartOverviewDomain;
  scaleTicks: Record<string, number[]>;
}

type IChartWithSelectionZoom = Pick<
  Chart,
  "canvas" | "width" | "height" | "chartArea" | "scales" | "draw" | "getActiveElements" | "setActiveElements"
> & {
  crosshair?: {
    enabled?: boolean;
    markerValue?: number | null;
  };
  selectionZoom?: {
    reset?: (notify?: boolean) => void;
    options?: {
      viewportMode?: boolean;
      xScaleId?: string;
      yScaleIds?: string[];
      cascadeDatasets?: Array<Array<{ x: number }>>;
      xOffset?: number;
      yOffset?: number;
    };
  };
};

type TOverviewChart = Chart<"line" | "radar" | "scatter">;

interface IChartOverviewSource {
  configuration: ChartConfiguration<"line" | "radar" | "scatter">;
  scaleBounds: Record<string, IAxisZoomBounds>;
  width: number;
  height: number;
  viewportMode: boolean;
}

const createOverviewSource = (
  chart: TOverviewChart & IChartWithSelectionZoom,
  original?: IChartOverviewSource,
): IChartOverviewSource => {
  const scaleBounds =
    original?.scaleBounds ??
    Object.fromEntries(Object.entries(chart.scales).map(([id, scale]) => [id, { min: scale.min, max: scale.max }]));
  return {
    width: original?.width ?? chart.width,
    height: original?.height ?? chart.height,
    viewportMode: original?.viewportMode ?? Boolean(chart.selectionZoom?.options?.viewportMode),
    scaleBounds,
    configuration: {
      type: (chart.config as ChartConfiguration<"line" | "radar" | "scatter">).type,
      data: original?.configuration.data ?? chart.data,
      plugins: chart.config.plugins?.filter(plugin => plugin.id !== "zoomOverview"),
      options: {
        ...chart.config.options,
        responsive: false,
        animation: false,
        devicePixelRatio: chart.currentDevicePixelRatio,
        events: [],
        scales: Object.fromEntries(
          Object.entries(scaleBounds).map(([id, bounds]) => [id, { ...chart.config.options?.scales?.[id], ...bounds }]),
        ),
        plugins: {
          ...chart.config.options?.plugins,
          htmlLegend: false,
          selectionZoom: false,
          crosshair: false,
          tooltip: { enabled: false },
        } as NonNullable<ChartConfiguration<"line" | "radar" | "scatter">["options"]>["plugins"],
      },
    },
  };
};

export const captureChartOverview = (
  chart: IChartWithSelectionZoom,
  viewportMode = chart.selectionZoom?.options?.viewportMode,
): IChartZoomOverview | null => {
  const options = chart.selectionZoom?.options;
  // Polar viewport zoom transforms the whole canvas; Cartesian zoom uses the plot area.
  const area = viewportMode ? { left: 0, top: 0, right: chart.width, bottom: chart.height } : chart.chartArea;
  const width = area.right - area.left;
  const height = area.bottom - area.top;
  if (width <= 0 || height <= 0 || chart.width <= 0) return null;

  const canvas = document.createElement("canvas");
  const pixelRatio = chart.canvas.width / chart.width;
  canvas.width = Math.max(1, Math.round(width * pixelRatio));
  canvas.height = Math.max(1, Math.round(height * pixelRatio));
  const context = canvas.getContext("2d");
  if (!context) return null;
  const activeElements = chart.getActiveElements();
  const crosshair = chart.crosshair;
  const crosshairState = crosshair && { enabled: crosshair.enabled, markerValue: crosshair.markerValue };
  try {
    chart.setActiveElements([]);
    if (crosshair) {
      crosshair.enabled = false;
      crosshair.markerValue = null;
    }
    drawWithoutFrequencyProfile(chart);
    context.drawImage(
      chart.canvas,
      area.left * pixelRatio,
      area.top * pixelRatio,
      width * pixelRatio,
      height * pixelRatio,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  } finally {
    if (crosshair && crosshairState) {
      crosshair.enabled = crosshairState.enabled;
      crosshair.markerValue = crosshairState.markerValue;
    }
    chart.setActiveElements(activeElements);
    chart.draw();
  }

  const xScale = chart.scales[options?.xScaleId ?? "x"];
  const yScaleIds = options?.yScaleIds ?? Object.keys(chart.scales).filter(id => chart.scales[id].axis === "y");
  const domain: IChartOverviewDomain = { y: {}, reverseY: {} };
  if (xScale) {
    domain.x = { min: xScale.min, max: xScale.max };
    domain.reverseX = Boolean(xScale.options.reverse);
  }
  yScaleIds.forEach(id => {
    const scale = chart.scales[id];
    if (!scale) return;
    domain.y[id] = { min: scale.min, max: scale.max };
    domain.reverseY![id] = Boolean(scale.options.reverse);
  });
  if (options?.cascadeDatasets) {
    domain.cascade = {
      datasets: options.cascadeDatasets,
      xOffset: options.xOffset ?? 0,
      yOffset: options.yOffset ?? 0,
    };
  }
  const scaleTicks = Object.fromEntries(
    Object.entries(chart.scales).map(([id, scale]) => [id, scale.ticks.map(tick => tick.value)]),
  );
  return { imageUrl: canvas.toDataURL(), width, height, domain, scaleTicks };
};

const refreshOverviewImage = (source: IChartOverviewSource, visibility: boolean[]): string | undefined => {
  const canvas = document.createElement("canvas");
  canvas.width = source.width;
  canvas.height = source.height;
  const { configuration } = source;
  const chart = new Chart<"line" | "radar" | "scatter">(canvas, {
    ...configuration,
    plugins: [
      ...(configuration.plugins ?? []),
      {
        id: "zoomOverviewDomain",
        afterLayout(preview) {
          Object.entries(source.scaleBounds).forEach(([id, bounds]) => {
            const scale = preview.scales[id];
            if (!scale) return;
            scale.min = bounds.min;
            scale.max = bounds.max;
            scale.configure?.();
          });
        },
      },
    ],
    data: {
      ...configuration.data,
      datasets: configuration.data.datasets.map((dataset, index) => ({
        ...dataset,
        data: dataset.data.slice(),
        hidden: !visibility[index],
      })),
    },
  });
  try {
    return captureChartOverview(chart, source.viewportMode)?.imageUrl;
  } finally {
    chart.destroy();
  }
};

export const CHART_SELECTION_EVENTS: Array<keyof HTMLElementEventMap> = [
  "mousedown",
  "mousemove",
  "mouseup",
  "mouseout",
  "mouseleave",
  "click",
  "touchstart",
  "touchmove",
  "touchend",
];

const clampViewportValue = (value: number): number => Math.min(1, Math.max(0, value));

const normalizeViewportZoomBounds = (bounds: IViewportZoomBounds | null | undefined): IViewportZoomBounds | null => {
  if (!bounds) {
    return null;
  }

  const rawBounds = [bounds.left, bounds.right, bounds.top, bounds.bottom];

  if (rawBounds.some(bound => !Number.isFinite(bound))) {
    return null;
  }

  const left = clampViewportValue(Math.min(bounds.left, bounds.right));
  const right = clampViewportValue(Math.max(bounds.left, bounds.right));
  const top = clampViewportValue(Math.min(bounds.top, bounds.bottom));
  const bottom = clampViewportValue(Math.max(bounds.top, bounds.bottom));

  if (left >= right || top >= bottom) {
    return null;
  }

  return { left, right, top, bottom };
};

export const hasChartZoomState = (zoomState: IChartZoomState | null | undefined): boolean => {
  const hasViewport = normalizeViewportZoomBounds(zoomState?.viewport) !== null;

  return Boolean(
    zoomState?.x ||
      (zoomState?.y && Object.keys(zoomState.y).length > 0) ||
      hasViewport ||
      zoomState?.itemRange ||
      zoomState?.selectionX ||
      zoomState?.selectionY,
  );
};

export const applyZoomBounds = <T extends { min?: number; max?: number }>(
  scaleConfig: T,
  bounds?: IAxisZoomBounds,
): T => {
  if (!bounds) {
    return scaleConfig;
  }

  return {
    ...scaleConfig,
    min: bounds.min,
    max: bounds.max,
  };
};

export const getCascadeZoomXBounds = <T extends { x: number }>(
  data: readonly { items: readonly T[] }[],
  itemRange: IItemRangeZoom,
): IAxisZoomBounds | null => {
  const source = data[itemRange.datasetIndex] ?? data[0];
  const startX = source?.items[itemRange.startIndex]?.x;
  const endX = source?.items[itemRange.endIndex]?.x;

  if (!Number.isFinite(startX) || !Number.isFinite(endX)) {
    return null;
  }

  return {
    min: Math.min(startX, endX),
    max: Math.max(startX, endX),
  };
};

export const sliceCascadeDataByItemRange = <T extends { items: Array<{ x: number }> }>(
  data: readonly T[],
  itemRange: IItemRangeZoom,
  selectionX?: IAxisZoomBounds,
): T[] => {
  const xBounds = selectionX ?? getCascadeZoomXBounds(data, itemRange);

  if (!xBounds) {
    return data.map(series => ({
      ...series,
      items: series.items.slice(itemRange.startIndex, itemRange.endIndex + 1),
    }));
  }

  return data.map(series => ({
    ...series,
    items: series.items.filter(item => item.x >= xBounds.min && item.x <= xBounds.max),
  }));
};

export const buildCascadeZoomBounds = (
  selection: IAxisZoomBounds | undefined,
  seriesCount: number,
  offset: number,
): IAxisZoomBounds | undefined => {
  if (!selection) {
    return undefined;
  }

  return {
    min: selection.min,
    max: selection.max + Math.max(0, seriesCount - 1) * offset,
  };
};

const normalizeZoomState = (zoomState: IChartZoomState | null): IChartZoomState | null => {
  if (!zoomState) {
    return null;
  }

  const nextState: IChartZoomState = {};

  if (zoomState.x) {
    nextState.x = zoomState.x;
  }

  if (zoomState.y && Object.keys(zoomState.y).length > 0) {
    nextState.y = zoomState.y;
  }

  const viewport = normalizeViewportZoomBounds(zoomState.viewport);
  if (viewport) {
    nextState.viewport = viewport;
  }

  if (zoomState.itemRange) {
    nextState.itemRange = zoomState.itemRange;
  }

  if (zoomState.selectionX) {
    nextState.selectionX = zoomState.selectionX;
  }

  if (zoomState.selectionY) {
    nextState.selectionY = zoomState.selectionY;
  }

  if (zoomState.baseDatasetIndex !== undefined) {
    nextState.baseDatasetIndex = zoomState.baseDatasetIndex;
  }

  return hasChartZoomState(nextState) ? nextState : null;
};

const composeViewportZoomBounds = (
  previousBounds: IViewportZoomBounds | undefined,
  nextBounds: IViewportZoomBounds | undefined,
): IViewportZoomBounds | null => {
  const normalizedNextBounds = normalizeViewportZoomBounds(nextBounds);

  if (!normalizedNextBounds) {
    return normalizeViewportZoomBounds(previousBounds);
  }

  const normalizedPreviousBounds = normalizeViewportZoomBounds(previousBounds);

  if (!normalizedPreviousBounds) {
    return normalizedNextBounds;
  }

  const previousWidth = normalizedPreviousBounds.right - normalizedPreviousBounds.left;
  const previousHeight = normalizedPreviousBounds.bottom - normalizedPreviousBounds.top;

  return normalizeViewportZoomBounds({
    left: normalizedPreviousBounds.left + normalizedNextBounds.left * previousWidth,
    right: normalizedPreviousBounds.left + normalizedNextBounds.right * previousWidth,
    top: normalizedPreviousBounds.top + normalizedNextBounds.top * previousHeight,
    bottom: normalizedPreviousBounds.top + normalizedNextBounds.bottom * previousHeight,
  });
};

export const mergeChartZoomState = (
  previousState: IChartZoomState | null,
  payload: IChartZoomPayload | ICascadeChartZoomPayload,
): IChartZoomState | null => {
  if (payload.axisMode === "viewport") {
    return normalizeZoomState({
      viewport: composeViewportZoomBounds(previousState?.viewport, payload.viewport) ?? undefined,
    });
  }

  if (payload.itemRange) {
    return normalizeZoomState({
      itemRange: payload.itemRange,
      y: payload.y,
      selectionX: payload.selectionX,
      selectionY: payload.selectionY,
      baseDatasetIndex: "baseDatasetIndex" in payload ? payload.baseDatasetIndex : undefined,
    });
  }

  const nextState: IChartZoomState = {
    x: payload.axisMode === "y" ? previousState?.x : payload.x ?? previousState?.x,
    y: payload.axisMode === "x" ? previousState?.y : payload.y ?? previousState?.y,
    selectionY: payload.selectionY ?? previousState?.selectionY,
    baseDatasetIndex: "baseDatasetIndex" in payload ? payload.baseDatasetIndex : undefined,
  };

  return normalizeZoomState(nextState);
};

export const useChartZoom = (
  chartRef: MutableRefObject<(TOverviewChart & IChartWithSelectionZoom) | null>,
  props: IChartZoomControlProps,
  dataKey: object = chartRef,
) => {
  const { registerReset, onZoomStateChange } = props;
  const [zoomState, setZoomState] = useState<IChartZoomState | null>(null);
  const overviewRef = useRef<IChartZoomOverview | null>(null);
  const sourceRef = useRef<IChartOverviewSource | null>(null);
  const visibilityRef = useRef<{ dataKey: object; values: boolean[] } | null>(null);
  const dataKeyRef = useRef(dataKey);
  dataKeyRef.current = dataKey;
  const [overview, setOverview] = useState<IChartZoomOverview | null>(null);

  const refreshOverviewFromChart = useCallback((chart: TOverviewChart) => {
    if (!overviewRef.current || !sourceRef.current) return;
    const visibility = chart.data.datasets.map((_, index) => chart.isDatasetVisible(index));
    visibilityRef.current = { dataKey: dataKeyRef.current, values: visibility };
    const source = createOverviewSource(chart, sourceRef.current);
    const imageUrl = refreshOverviewImage(source, visibility);
    if (!imageUrl) return;
    sourceRef.current = source;
    overviewRef.current = { ...overviewRef.current, imageUrl };
    setOverview(overviewRef.current);
  }, []);

  const refreshOverview = useCallback(() => {
    if (chartRef.current) refreshOverviewFromChart(chartRef.current);
  }, [chartRef, refreshOverviewFromChart]);

  const overviewPlugin = useMemo<Plugin<"line" | "radar" | "scatter">>(() => {
    return {
      id: "zoomOverview",
      afterInit(chart) {
        if (visibilityRef.current?.dataKey !== dataKey) return;
        visibilityRef.current.values.forEach((visible, index) => {
          if (index < chart.data.datasets.length) chart.setDatasetVisibility(index, visible);
        });
      },
      afterUpdate(chart) {
        const nextVisibility = chart.data.datasets.map((_, index) => chart.isDatasetVisible(index));
        const visibility = visibilityRef.current?.dataKey === dataKey ? visibilityRef.current.values : null;
        const changed = visibility?.some((visible, index) => visible !== nextVisibility[index]);
        visibilityRef.current = { dataKey, values: nextVisibility };
        if (!changed || !overviewRef.current || !sourceRef.current) return;
        refreshOverviewFromChart(chart as TOverviewChart);
      },
    };
  }, [dataKey, refreshOverviewFromChart]);

  const isZoomed = useMemo(() => hasChartZoomState(zoomState), [zoomState]);

  const applySelectionZoom = useCallback(
    (payload: IChartZoomPayload) => {
      if (!overviewRef.current && chartRef.current) {
        overviewRef.current = captureChartOverview(chartRef.current);
        sourceRef.current = createOverviewSource(chartRef.current);
        setOverview(overviewRef.current);
      }
      setZoomState(previousState => mergeChartZoomState(previousState, payload));
    },
    [chartRef],
  );

  const panZoom = useCallback((initialState: IChartZoomState, deltaX: number, deltaY: number) => {
    const overview = overviewRef.current;
    if (!overview) return;
    setZoomState(currentState => panChartOverview(initialState, overview.domain, deltaX, deltaY) ?? currentState);
  }, []);

  const clearZoomState = useCallback(() => {
    overviewRef.current = null;
    sourceRef.current = null;
    setOverview(null);
    setZoomState(null);
  }, []);

  const resetZoom = useCallback(() => {
    chartRef.current?.selectionZoom?.reset?.(false);
    clearZoomState();
  }, [chartRef, clearZoomState]);

  useEffect(() => {
    onZoomStateChange?.(isZoomed);
  }, [isZoomed, onZoomStateChange]);

  useEffect(() => {
    registerReset?.(resetZoom);

    return () => {
      registerReset?.(null);
    };
  }, [registerReset, resetZoom]);

  useEffect(() => {
    return () => {
      onZoomStateChange?.(false);
    };
  }, [onZoomStateChange]);

  useEffect(() => {
    return () => {
      overviewRef.current = null;
      sourceRef.current = null;
      visibilityRef.current = null;
    };
  }, []);

  return {
    zoomState,
    isZoomed,
    applySelectionZoom,
    clearZoomState,
    resetZoom,
    overview: isZoomed ? overview : null,
    overviewPlugin,
    refreshOverview,
    panZoom,
  };
};
